import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import { useWorkspaceSettings } from '@/hooks/useWorkspaceSettings';
import { useSetAtom } from 'jotai';
import { callSettingsAtom, DEFAULT_CALL_THRESHOLDS, CallThreshold } from '@/lib/atoms';

const COLOR_PRESETS = [
  { label: 'Green', value: '#22c55e' },
  { label: 'Yellow', value: '#eab308' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Pink', value: '#ec4899' },
  { label: 'Teal', value: '#14b8a6' },
];

function formatTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

export function CallSettings() {
  const { settings, loading, updateSettings } = useWorkspaceSettings();
  const setCallSettings = useSetAtom(callSettingsAtom);
  const [thresholds, setThresholds] = useState<CallThreshold[]>(DEFAULT_CALL_THRESHOLDS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.call_thresholds && settings.call_thresholds.length > 0) {
      setThresholds(settings.call_thresholds);
    }
  }, [settings]);

  const updateThresholdMinutes = (index: number, value: string) => {
    setThresholds(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], minutes: Math.max(0, Number(value)) };
      return updated;
    });
  };

  const updateThresholdColor = (index: number, color: string) => {
    setThresholds(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], color };
      return updated;
    });
  };

  const addThreshold = () => {
    const maxMinutes = Math.max(...thresholds.map(t => t.minutes));
    const usedColors = new Set(thresholds.map(t => t.color));
    const nextColor = COLOR_PRESETS.find(c => !usedColors.has(c.value))?.value ?? COLOR_PRESETS[0].value;
    setThresholds(prev => [...prev, { minutes: maxMinutes + 5, color: nextColor }]);
  };

  const removeThreshold = (index: number) => {
    if (thresholds[index].minutes === 0) return;
    setThresholds(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    const sorted = [...thresholds].sort((a, b) => a.minutes - b.minutes);
    try {
      await updateSettings({ call_thresholds: sorted });
      setCallSettings(sorted);
      setThresholds(sorted);
    } catch {
      // updateSettings handles its own error toast
    } finally {
      setSaving(false);
    }
  };

  const sortedForPreview = [...thresholds].sort((a, b) => a.minutes - b.minutes);

  if (loading) {
    return <div className="p-4 text-muted-foreground">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Call Settings</h2>
        <p className="text-muted-foreground">
          Configure timer color thresholds for phone call logging.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Timer Thresholds</CardTitle>
          <CardDescription>
            Set the duration thresholds at which the call timer changes color. The timer will use the color of the highest threshold that has been reached.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {thresholds.map((threshold, index) => (
            <div key={index} className="flex items-center gap-3">
              <Label className="text-sm text-muted-foreground w-14 shrink-0">After</Label>
              <Input
                type="number"
                min={0}
                value={threshold.minutes}
                onChange={(e) => updateThresholdMinutes(index, e.target.value)}
                className="w-20"
                disabled={threshold.minutes === 0 && index === 0}
              />
              <Label className="text-sm text-muted-foreground shrink-0">min</Label>

              <div className="flex items-center gap-1.5 ml-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    title={preset.label}
                    onClick={() => updateThresholdColor(index, preset.value)}
                    className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      backgroundColor: preset.value,
                      borderColor: threshold.color === preset.value ? 'white' : 'transparent',
                      boxShadow: threshold.color === preset.value ? `0 0 0 2px ${preset.value}` : 'none',
                    }}
                  />
                ))}
              </div>

              <div className="ml-auto">
                {threshold.minutes === 0 && index === 0 ? (
                  <div className="w-9" />
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeThreshold(index)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={addThreshold} className="mt-2">
            <Plus className="h-4 w-4 mr-1" />
            Add Tier
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            How the timer will look at each threshold.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {sortedForPreview.map((threshold, index) => (
              <div key={index} className="flex flex-col items-center gap-1.5">
                <span
                  className="text-2xl font-mono font-bold tabular-nums"
                  style={{ color: threshold.color }}
                >
                  {formatTime(threshold.minutes * 60)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {threshold.minutes === 0 ? 'Start' : `${threshold.minutes} min`}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
